import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, addDays } from 'date-fns';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useSocket } from '../context/SocketContext';
import toast from 'react-hot-toast';
import { useSEO } from '../hooks/useSEO';

const pad = n => String(n).padStart(2,'0');

// ── Live countdown timer ──────────────────────────────────────────
function LiveCountdown({ endsAt }) {
  const [timeLeft, setTimeLeft] = useState('');
  const [urgent,   setUrgent]   = useState(false);

  useEffect(() => {
    if (!endsAt) return;
    const tick = () => {
      const diff = new Date(endsAt) - new Date();
      if (diff <= 0) { setTimeLeft('Auction ended'); return; }

      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000)  / 60000);
      const s = Math.floor((diff % 60000)    / 1000);

      setUrgent(diff < 3600000); // under 1 hour = urgent

      if (d > 0)      setTimeLeft(`${d}d ${pad(h)}h ${pad(m)}m`);
      else if (h > 0) setTimeLeft(`${pad(h)}h ${pad(m)}m ${pad(s)}s`);
      else            setTimeLeft(`${pad(m)}m ${pad(s)}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endsAt]);

  if (!timeLeft) return null;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 14px', borderRadius:8, background: urgent ? '#FDECEA' : '#F5F0EB', border: `1px solid ${urgent ? 'rgba(192,57,43,.2)' : 'var(--border)'}` }}>
      <span style={{ fontSize:16 }}>{urgent ? '🔴' : '⏱'}</span>
      <span style={{ fontWeight:700, fontSize:15, color: urgent ? 'var(--cherry)' : 'var(--text-primary)', fontVariantNumeric:'tabular-nums' }}>
        {timeLeft}
      </span>
      <span style={{ fontSize:12, color:'var(--text-soft)' }}>remaining</span>
    </div>
  );
}

function SimilarCountdown({ endsAt }) {
  const [label, setLabel] = useState('');
  const [urgent, setUrgent] = useState(false);
  useEffect(() => {
    if (!endsAt) return;
    const tick = () => {
      const diff = new Date(endsAt) - new Date();
      if (diff <= 0) { setLabel('Ended'); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setUrgent(diff < 3600000);
      if (h > 23) setLabel(`${Math.floor(h/24)}d ${h%24}h`);
      else if (h > 0) setLabel(`${pad(h)}h ${pad(m)}m`);
      else setLabel(`${pad(m)}m ${pad(s)}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endsAt]);
  if (!label) return null;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, fontWeight:700, color: urgent ? '#FF6B6B' : '#E8D5B7', fontVariantNumeric:'tabular-nums' }}>
      <span>{urgent ? '🔴' : '⏱'}</span>
      <span>{label}</span>
    </div>
  );
}

function StarRating({ rating, count, onClick }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6, cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <span className="stars">{'★'.repeat(Math.round(rating))}{'☆'.repeat(5 - Math.round(rating))}</span>
      <span style={{ fontWeight:700, fontSize:14 }}>{parseFloat(rating||0).toFixed(1)}</span>
      <span style={{ fontSize:13, color:'var(--text-soft)' }}>({count || 0} reviews)</span>
    </div>
  );
}

export default function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { joinAuction, leaveAuction, onNewBid, onOutbid } = useSocket() || {};

  const [listing,    setListing]    = useState(null);

  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api','') || '';
  const primaryImage = listing?.images?.[0]?.url || listing?.primary_image;
  useSEO(listing ? {
    title: listing.title,
    description: listing.description ? listing.description.substring(0, 155) : undefined,
    image: primaryImage ? `${API_BASE}${primaryImage}` : undefined,
    url: `/listing/${listing.slug || listing.id}`,
    type: 'product',
  } : {});
  const [loading,    setLoading]    = useState(true);
  const [activeImg,  setActiveImg]  = useState(0);
  const [saved,      setSaved]      = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [watching,   setWatching]   = useState(false);
  const [watchLoading, setWatchLoading] = useState(false);
  const [showMsg,    setShowMsg]    = useState(false);
  const [msgBody,    setMsgBody]    = useState('');
  const [msgSent,    setMsgSent]    = useState(false);
  const [bidAmount,  setBidAmount]  = useState('');
  const [bidError,   setBidError]   = useState('');
  const [bidLoading, setBidLoading] = useState(false);
  const [showOffer,  setShowOffer]  = useState(false);
  const [offerAmount, setOfferAmount] = useState('');
  const [offerMsg,   setOfferMsg]   = useState('');
  const [offerSent,  setOfferSent]  = useState(false);
  const [following,  setFollowing]  = useState(false);
  const [offerLoading, setOfferLoading] = useState(false);
  const [rentDates,  setRentDates]  = useState({ start: format(new Date(),'yyyy-MM-dd'), end: format(addDays(new Date(),3),'yyyy-MM-dd') });
  const [recentBids, setRecentBids] = useState([]);
  const [bidFlash,   setBidFlash]   = useState(false); // flash animation on new bid
  const [similar,    setSimilar]    = useState([]);
  const [userCoords, setUserCoords] = useState(null);

  // Accepted offer for this listing
  const [acceptedOffer, setAcceptedOffer] = useState(null);

  // Barter trade flow
  const [showTradePanel,      setShowTradePanel]      = useState(false);
  const [userListings,        setUserListings]        = useState([]);
  const [userListingsLoading, setUserListingsLoading] = useState(false);
  const [selectedTradeId,     setSelectedTradeId]     = useState(null);
  const [tradeLoading,        setTradeLoading]        = useState(false);
  const [tradeSent,           setTradeSent]           = useState(false);

  // Free claim flow
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimSent,    setClaimSent]    = useState(false);

  // Price history / sold comps
  const [priceHistory, setPriceHistory] = useState([]);
  const [soldComps,    setSoldComps]    = useState([]);

  useEffect(() => {
    API.get(`/listings/${id}`)
      .then(r => {
        setListing(r.data.listing);
        setSaved(r.data.listing.isSaved);
        // Track view (fire-and-forget)
        API.post(`/analytics/view/${id}`).catch(() => {});
        if (user && r.data.listing.seller_id !== user.id) {
          API.get(`/users/${r.data.listing.seller_id}/follow-status`).then(fr => setFollowing(fr.data.following)).catch(()=>{});
          API.get(`/watchlist/watches/${r.data.listing.id}/status`).then(wr => setWatching(wr.data.watching)).catch(()=>{});
        }
        setRecentBids(r.data.listing.recentBids || []);
      })
      .catch(() => navigate('/browse'))
      .finally(() => setLoading(false));
    API.get(`/listings/${id}/similar`).then(r => setSimilar(r.data.listings || [])).catch(() => {});
    API.get(`/listings/${id}/price-history`).then(r => {
      setPriceHistory(r.data.history || []);
      setSoldComps(r.data.soldComps || []);
    }).catch(() => {});
    if (user) {
      API.get('/offers/mine').then(r => {
        const o = (r.data.offers || []).find(o => o.listing_id == id && o.status === 'accepted');
        if (o) setAcceptedOffer(o);
      }).catch(() => {});
    }
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {}
      );
    }
  }, [id]);

  // ── Socket.io — join auction room and listen for bids ───────────
  useEffect(() => {
    if (!listing || listing.listing_type !== 'auction') return;

    joinAuction?.(listing.id);

    // Listen for new bids from any user
    const offNewBid = onNewBid?.((data) => {
      if (data.listingId !== listing.id) return;
      setListing(prev => ({ ...prev, current_bid: data.amount, bid_count: data.bidCount }));
      setRecentBids(prev => [{
        amount:     data.amount,
        created_at: data.timestamp,
        username:   data.bidder.username || data.bidder.firstName,
        first_name: data.bidder.firstName,
      }, ...prev.slice(0, 9)]);
      // Flash the price to draw attention
      setBidFlash(true);
      setTimeout(() => setBidFlash(false), 1200);
      toast(`🔨 New bid: $${data.amount.toLocaleString()} by ${data.bidder.firstName}`, { duration: 3000 });
    });

    // Listen for outbid notifications (personal room)
    const offOutbid = onOutbid?.((data) => {
      if (data.listingId !== listing.id) return;
      toast.error(`⚠️ You've been outbid on "${data.title}"! New bid: $${data.newBid.toLocaleString()}`, { duration: 5000 });
    });

    return () => {
      leaveAuction?.(listing.id);
      offNewBid?.();
      offOutbid?.();
    };
  }, [listing?.id, listing?.listing_type]);

  const handleSave = async () => {
    if (!user) { navigate('/login'); return; }
    setSaving(true);
    const { data } = await API.post(`/listings/${id}/save`);
    setSaved(data.saved);
    setSaving(false);
  };

  const handleWatch = async () => {
    if (!user) { navigate('/login'); return; }
    setWatchLoading(true);
    const { data } = await API.post(`/watchlist/watches/${id}`);
    setWatching(data.watching);
    setWatchLoading(false);
  };

  const handleBid = async () => {
    if (!user) { navigate('/login'); return; }
    const minBid = parseFloat(listing.current_bid || listing.starting_bid || 0);
    if (!bidAmount || parseFloat(bidAmount) <= minBid) {
      setBidError(`Bid must be higher than $${minBid.toLocaleString()}`);
      return;
    }
    setBidLoading(true);
    try {
      const { data } = await API.post(`/listings/${id}/bid`, { amount: parseFloat(bidAmount) });
      // Local update as fallback (socket will also fire for other users)
      setListing(p => ({ ...p, current_bid: data.currentBid, bid_count: data.bidCount || (p.bid_count||0)+1 }));
      setBidAmount('');
      setBidError('');
      toast.success(`✅ Bid of $${parseFloat(bidAmount).toLocaleString()} placed!`);
    } catch (err) {
      setBidError(err.response?.data?.error || 'Bid failed');
    } finally { setBidLoading(false); }
  };

  const handleSendMessage = async () => {
    if (!user) { navigate('/login'); return; }
    await API.post('/messages', { recipientId: listing.seller_id, listingId: listing.id, body: msgBody });
    setMsgSent(true);
  };

  const getRentDays = () => {
    const d = Math.ceil((new Date(rentDates.end) - new Date(rentDates.start)) / 86400000);
    return Math.max(1, d);
  };

  const rentTotal = () => listing ? (parseFloat(listing.price||0) * getRentDays() + parseFloat(listing.deposit_amount||0) + parseFloat(listing.price||0) * getRentDays() * 0.10) : 0;

  const conditionLabel = { new:'New',like_new:'Like New',excellent:'Excellent',very_good:'Very Good',good:'Good',fair:'Fair' };

  const haversineMi = (lat1, lng1, lat2, lng2) => {
    const R = 3958.8;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)**2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  };

  const nearbyMiles = 25;

  if (loading) return (
    <div style={{ maxWidth:1100, margin:'0 auto', padding:'48px', minHeight:'60vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ fontSize:48, marginBottom:16 }} className="float-anim">🍒</div>
        <div style={{ fontSize:14, color:'var(--text-soft)' }}>Loading listing...</div>
      </div>
    </div>
  );

  if (!listing) return null;

  const images = listing.images?.length ? listing.images : [{ url: null }];
  const isOwner = user?.id === listing.seller_id;

  return (
    <div className='listing-detail-page'>
      {/* Back & Breadcrumb */}
      <div style={{ fontSize:13, color:'var(--text-soft)', marginBottom:20, display:'flex', gap:6, alignItems:'center' }}>
        <button onClick={() => navigate(-1)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--cherry)', fontWeight:600, padding:0, fontSize:13 }}>← Back</button>
        <span>·</span>
        <span style={{ cursor:'pointer', color:'var(--cherry)' }} onClick={() => navigate('/browse')}>Browse</span>
        <span>›</span>
        <span style={{ cursor:'pointer', color:'var(--cherry)' }} onClick={() => navigate(`/browse?category=${listing.category_slug}`)}>{listing.category_label}</span>
        <span>›</span>
        <span>{listing.title}</span>
      </div>

      <div className='listing-detail-grid'>
        {/* LEFT — images + details */}
        <div>
          {/* Main image */}
          <div style={{ borderRadius:16, overflow:'hidden', marginBottom:12, aspectRatio:'4/3', background:'var(--cream)', display:'flex', alignItems:'center', justifyContent:'center', position:'relative' }}>
            {images[activeImg]?.url
              ? <img src={`${API_BASE}${images[activeImg].url}`} alt={listing.title} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
              : <span style={{ fontSize:80, opacity:.4 }}>📦</span>
            }
            {/* Badge */}
            <div style={{ position:'absolute', top:14, left:14, background: listing.listing_type==='rent'?'#1A5276': listing.listing_type==='auction'?'#C0392B':'#2D7A3A', color:'white', fontSize:11, fontWeight:700, padding:'4px 10px', borderRadius:6, letterSpacing:'1.5px', textTransform:'uppercase' }}>
              {listing.listing_type==='rent'?'For Rent': listing.listing_type==='auction'?'Live Bid':'For Sale'}
            </div>
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div style={{ display:'flex', gap:8, marginBottom:28, flexWrap:'wrap' }}>
              {images.map((img, i) => (
                <div key={i} onClick={() => setActiveImg(i)}
                  style={{ width:72, height:72, borderRadius:10, overflow:'hidden', border:`2px solid ${activeImg===i?'var(--cherry)':'var(--border)'}`, cursor:'pointer', background:'var(--cream)', flexShrink:0 }}>
                  {img.url ? <img src={`${API_BASE}${img.url}`} alt="" style={{ width:'100%',height:'100%',objectFit:'cover' }} /> : <span style={{ fontSize:28, display:'flex', alignItems:'center', justifyContent:'center', height:'100%' }}>📦</span>}
                </div>
              ))}
            </div>
          )}

          {/* Category + title */}
          <div style={{ fontSize:11, fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'1.5px', marginBottom:6 }}>
            {listing.category_icon} {listing.category_label}
          </div>
          <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:32, fontWeight:700, lineHeight:1.2, marginBottom:12 }}>{listing.title}</h1>

          {/* Rating + location */}
          <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:20, flexWrap:'wrap' }}>
            <StarRating rating={listing.rating_avg} count={listing.rating_count} onClick={() => navigate(`/profile/${listing.seller_id}`)} />
            <span style={{ color:'var(--border)' }}>|</span>
            <span style={{ fontSize:13, color:'var(--text-soft)' }}>📍 {listing.location_city}, {listing.location_state}</span>
            {listing.condition && (
              <>
                <span style={{ color:'var(--border)' }}>|</span>
                <span style={{ fontSize:13, color:'var(--text-soft)' }}>Condition: <strong style={{ color:'var(--text-primary)' }}>{conditionLabel[listing.condition]}</strong></span>
              </>
            )}
          </div>

          {/* Stock availability badge */}
          {listing.quantity && listing.quantity > 1 && (
            <div style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'8px 12px', background:'#E3F2FD', borderRadius:8, marginBottom:20, fontSize:13, fontWeight:600, color:'#1565C0' }}>
              📦 {listing.quantity === 1 ? 'Only 1 left' : `${listing.quantity} available in stock`}
            </div>
          )}

          {/* Description */}
          <div style={{ background:'var(--cream)', borderRadius:12, padding:'20px 22px', marginBottom:24 }}>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, marginBottom:10 }}>Description</div>
            <p style={{ fontSize:14, color:'var(--text-mid)', lineHeight:1.75, whiteSpace:'pre-wrap' }}>{listing.description}</p>
          </div>

          {/* Barter wants */}
          {listing.listing_type === 'barter' && listing.barter_wants && (
            <div style={{ background:'#F3E5F5', border:'1px solid #CE93D8', borderRadius:12, padding:'20px 22px', marginBottom:24 }}>
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, marginBottom:10, color:'#6A1B9A' }}>🔄 Looking to trade for</div>
              <p style={{ fontSize:14, color:'#4A148C', lineHeight:1.75, whiteSpace:'pre-wrap', margin:0 }}>{listing.barter_wants}</p>
            </div>
          )}

          {/* Details grid */}
          {(listing.brand || listing.model || listing.condition) && (
            <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:'18px 22px', marginBottom:24 }}>
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, marginBottom:14 }}>Item Details</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                {listing.brand && <div><span style={{ fontSize:12, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'1px' }}>Brand</span><div style={{ fontWeight:600, marginTop:2 }}>{listing.brand}</div></div>}
                {listing.model && <div><span style={{ fontSize:12, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'1px' }}>Model</span><div style={{ fontWeight:600, marginTop:2 }}>{listing.model}</div></div>}
                {listing.year  && <div><span style={{ fontSize:12, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'1px' }}>Year</span><div style={{ fontWeight:600, marginTop:2 }}>{listing.year}</div></div>}
                {listing.condition && <div><span style={{ fontSize:12, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'1px' }}>Condition</span><div style={{ fontWeight:600, marginTop:2 }}>{conditionLabel[listing.condition]}</div></div>}
                <div><span style={{ fontSize:12, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'1px' }}>Listed</span><div style={{ fontWeight:600, marginTop:2 }}>{listing.published_at ? format(new Date(listing.published_at),'MMM d, yyyy') : 'Recently'}</div></div>
              </div>
            </div>
          )}

          {/* Meetup spots */}
          {listing.meetupSpots?.length > 0 && (
            <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:'18px 22px', marginBottom:24 }}>
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, marginBottom:14 }}>📍 Meetup Spots</div>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {listing.meetupSpots.map((s,i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px', background:'var(--cream)', borderRadius:10 }}>
                    <span style={{ fontSize:20 }}>📍</span>
                    <div>
                      <div style={{ fontWeight:600, fontSize:14 }}>{s.name}</div>
                      {s.address && <div style={{ fontSize:12, color:'var(--text-soft)' }}>{s.address}</div>}
                    </div>
                    {s.distance_mi && <div style={{ marginLeft:'auto', fontSize:12, color:'var(--text-soft)' }}>{s.distance_mi} mi</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Auction bids */}
          {listing.listing_type === 'auction' && (
            <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, padding:'18px 22px', marginBottom:24 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
                <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700 }}>🔨 Live Bids</div>
                <div style={{ display:'flex', alignItems:'center', gap:5, padding:'3px 8px', background:'#FDECEA', borderRadius:100, fontSize:11, fontWeight:700, color:'var(--cherry)' }}>
                  <span style={{ width:6, height:6, borderRadius:'50%', background:'var(--cherry)', display:'inline-block', animation:'pulse 1.5s infinite' }}/>
                  LIVE
                </div>
                <span style={{ marginLeft:'auto', fontSize:12, color:'var(--text-soft)' }}>
                  {listing.bid_count||0} bid{listing.bid_count!==1?'s':''}
                </span>
              </div>
              {!recentBids.length ? (
                <div style={{ fontSize:13, color:'var(--text-soft)', textAlign:'center', padding:'16px 0' }}>
                  No bids yet — be the first!
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {recentBids.slice(0,8).map((b,i) => (
                    <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom: i < recentBids.length-1 ? '1px solid var(--border)' : 'none', animation: i===0 && bidFlash ? 'fadeUp .3s ease' : 'none' }}>
                      <div style={{ fontSize:13, color:'var(--text-mid)', display:'flex', alignItems:'center', gap:8 }}>
                        {i===0 && <span style={{ color:'var(--cherry)', fontWeight:700 }}>👑</span>}
                        <span style={{ fontWeight: i===0 ? 700 : 400 }}>{b.username || b.first_name}</span>
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <span style={{ fontSize:11, color:'var(--text-soft)' }}>
                          {b.created_at ? format(new Date(b.created_at), 'h:mm a') : ''}
                        </span>
                        <span style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:16, color: i===0 ? 'var(--cherry)' : 'var(--text-primary)' }}>
                          ${parseFloat(b.amount).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT — action card */}
        <div className='listing-detail-sticky'>
          <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:16, overflow:'hidden', boxShadow:'var(--shadow-md)' }}>
            {/* Price header */}
            <div style={{ background:'#1A1A1A', padding:'20px 22px' }}>
              {listing.listing_type === 'auction' && (
                <div style={{ fontSize:11, color:'#888', textTransform:'uppercase', letterSpacing:'1px', marginBottom:4 }}>
                  Current bid · {listing.bid_count||0} bid{listing.bid_count!==1?'s':''}
                </div>
              )}
              {listing.listing_type === 'rent' && <div style={{ fontSize:11, color:'#888', textTransform:'uppercase', letterSpacing:'1px', marginBottom:4 }}>Daily rate</div>}
              {listing.listing_type === 'sale' && <div style={{ fontSize:11, color:'#888', textTransform:'uppercase', letterSpacing:'1px', marginBottom:4 }}>Sale price</div>}
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:32, fontWeight:700, color:'white', transition:'color .4s', ...(bidFlash?{color:'#E74C3C'}:{}) }}>
                ${parseFloat(listing.listing_type==='auction' ? (listing.current_bid||listing.starting_bid||0) : listing.price||0).toLocaleString()}
                {listing.listing_type==='rent' && <span style={{ fontSize:16, fontWeight:400, color:'#888', fontFamily:"'DM Sans',sans-serif" }}> /day</span>}
              </div>
              {listing.listing_type === 'auction' && listing.auction_ends_at && (
                <div style={{ marginBottom: 12 }}>
                  <LiveCountdown endsAt={listing.auction_ends_at} />
                </div>
              )}
            </div>

            <div style={{ padding:22 }}>
              {/* Rent — date picker */}
              {listing.listing_type === 'rent' && !isOwner && (
                <div style={{ marginBottom:18 }}>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
                    <div>
                      <div style={{ fontSize:11, fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:5 }}>Start Date</div>
                      <input type="date" className="form-input" style={{ fontSize:13 }} value={rentDates.start}
                        min={format(new Date(),'yyyy-MM-dd')} onChange={e => setRentDates(p=>({...p,start:e.target.value}))} />
                    </div>
                    <div>
                      <div style={{ fontSize:11, fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:5 }}>End Date</div>
                      <input type="date" className="form-input" style={{ fontSize:13 }} value={rentDates.end}
                        min={rentDates.start} onChange={e => setRentDates(p=>({...p,end:e.target.value}))} />
                    </div>
                  </div>
                  <div style={{ background:'var(--cream)', borderRadius:10, padding:'12px 14px', fontSize:13 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
                      <span>${parseFloat(listing.price).toFixed(2)} × {getRentDays()} day{getRentDays()!==1?'s':''}</span>
                      <span>${(listing.price * getRentDays()).toFixed(2)}</span>
                    </div>
                    {listing.deposit_amount > 0 && (
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5, color:'var(--text-soft)' }}>
                        <span>Security deposit (refundable)</span>
                        <span>${parseFloat(listing.deposit_amount).toFixed(2)}</span>
                      </div>
                    )}
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5, color:'var(--text-soft)' }}>
                      <span>Platform fee (10%)</span>
                      <span>${(listing.price * getRentDays() * 0.1).toFixed(2)}</span>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontWeight:700, paddingTop:8, borderTop:'1px solid var(--border)' }}>
                      <span>Total</span>
                      <span>${rentTotal().toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Auction — bid input */}
              {listing.listing_type === 'auction' && !isOwner && (
                <div style={{ marginBottom:18 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:'var(--text-soft)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:8 }}>
                    Your Bid (min ${((listing.current_bid||listing.starting_bid||0)+1).toLocaleString()})
                  </div>
                  <div className="price-input-wrap" style={{ marginBottom:8 }}>
                    <span className="price-prefix">$</span>
                    <input className="price-field" type="number" placeholder="0.00" value={bidAmount}
                      onChange={e=>{setBidAmount(e.target.value);setBidError('');}} min={(listing.current_bid||listing.starting_bid||0)+1} step="0.01" />
                  </div>
                  {bidError && <div className="form-error">{bidError}</div>}
                </div>
              )}

              {/* CTA buttons */}
              {!isOwner ? (
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {listing.listing_type === 'auction' ? (
                    <>
                      <div style={{ display:'flex', flexDirection: listing.buy_it_now_enabled ? 'column' : 'row', gap:10 }}>
                        <button className="btn btn-cherry btn-full btn-lg" onClick={handleBid} disabled={bidLoading}>
                          {bidLoading ? <><span className="spinner"/>Placing bid...</> : '🔨 Place Bid'}
                        </button>
                        {listing.buy_it_now_enabled && listing.buy_it_now_price && (
                          <button className="btn btn-ghost btn-full btn-lg" onClick={() => {
                            addToCart(listing, { buyItNow: true });
                            toast.success('Added to cart! 🛒');
                          }}>
                            💰 Buy It Now<br/><span style={{ fontSize:11, fontWeight:400 }}>${parseFloat(listing.buy_it_now_price).toLocaleString('en-US',{minimumFractionDigits:2})}</span>
                          </button>
                        )}
                      </div>
                    </>
                  ) : listing.listing_type === 'barter' ? (
                    tradeSent ? (
                      <div className="alert alert-success" style={{ display:'flex', flexDirection:'column', gap:8 }}>
                        <div>✅ Trade proposal sent! The seller will review it.</div>
                        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/dashboard/barter')}>View in Barter Trades →</button>
                      </div>
                    ) : (
                      <>
                        <button className="btn btn-cherry btn-full btn-lg" onClick={() => {
                          if (!user) { navigate('/login'); return; }
                          if (!showTradePanel) {
                            setUserListingsLoading(true);
                            API.get('/users/me/listings?status=active').then(r => setUserListings(r.data.listings || [])).catch(() => []).finally(() => setUserListingsLoading(false));
                          }
                          setShowTradePanel(p => !p);
                        }}>
                          🔄 Propose Trade
                        </button>
                        {showTradePanel && (
                          <div style={{ background:'var(--cream)', borderRadius:12, padding:16, border:'1px solid var(--border)', marginTop:4 }}>
                            <div style={{ fontWeight:700, fontSize:14, marginBottom:12 }}>Which of your items will you offer?</div>
                            {userListingsLoading ? (
                              <div style={{ fontSize:13, color:'var(--text-soft)', padding:'12px 0' }}>Loading your listings...</div>
                            ) : !userListings.length ? (
                              <div style={{ fontSize:13, color:'var(--text-soft)' }}>
                                You don't have any active listings to offer.{' '}
                                <span style={{ color:'var(--cherry)', cursor:'pointer', fontWeight:600 }} onClick={() => navigate('/list')}>List an item first →</span>
                              </div>
                            ) : (
                              <div style={{ display:'flex', flexDirection:'column', gap:8, maxHeight:240, overflowY:'auto' }}>
                                {userListings.map(l => (
                                  <div key={l.id} onClick={() => setSelectedTradeId(l.id)}
                                    style={{ padding:'10px 12px', borderRadius:8, border:`2px solid ${selectedTradeId===l.id?'var(--cherry)':'var(--border)'}`, background:selectedTradeId===l.id?'var(--cherry-light)':'white', cursor:'pointer', display:'flex', alignItems:'center', gap:10, transition:'all .15s' }}>
                                    {l.primary_image
                                      ? <img src={`${API_BASE}${l.primary_image}`} alt="" style={{ width:44, height:44, borderRadius:6, objectFit:'cover', flexShrink:0 }} />
                                      : <div style={{ width:44, height:44, borderRadius:6, background:'var(--border)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>📦</div>
                                    }
                                    <div style={{ flex:1, minWidth:0 }}>
                                      <div style={{ fontWeight:600, fontSize:13, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.title}</div>
                                      <div style={{ fontSize:11, color:'var(--text-soft)', marginTop:2 }}>
                                        {l.listing_type} {l.price ? `· $${parseFloat(l.price).toLocaleString('en-US',{minimumFractionDigits:2})}` : ''}
                                      </div>
                                    </div>
                                    {selectedTradeId === l.id && <span style={{ color:'var(--cherry)', fontSize:18, flexShrink:0 }}>✓</span>}
                                  </div>
                                ))}
                              </div>
                            )}
                            {selectedTradeId && (
                              <button className="btn btn-cherry btn-full" style={{ marginTop:12 }} disabled={tradeLoading}
                                onClick={async () => {
                                  setTradeLoading(true);
                                  try {
                                    await API.post('/barters/request', { initiatorListingId: selectedTradeId, responderListingId: listing.id });
                                    setTradeSent(true);
                                    setShowTradePanel(false);
                                    toast.success('Trade proposal sent! 🔄');
                                  } catch (err) {
                                    const msg = err.response?.data?.error;
                                    if (msg === 'subscription_required') toast.error('A subscription is required to propose trades.');
                                    else toast.error(msg || 'Failed to send trade proposal');
                                  } finally { setTradeLoading(false); }
                                }}>
                                {tradeLoading ? 'Sending...' : '🔄 Send Trade Proposal'}
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )
                  ) : listing.listing_type === 'free' ? (
                    claimSent ? (
                      <div className="alert alert-success" style={{ display:'flex', flexDirection:'column', gap:8 }}>
                        <div>✅ Item claimed! The owner will be in touch to arrange pickup.</div>
                        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/dashboard/free')}>View in Free Claims →</button>
                      </div>
                    ) : (
                      <button className="btn btn-cherry btn-full btn-lg" disabled={claimLoading}
                        onClick={async () => {
                          if (!user) { navigate('/login'); return; }
                          setClaimLoading(true);
                          try {
                            await API.post('/free/claim', { listingId: listing.id });
                            setClaimSent(true);
                            toast.success('Item claimed! 🆓');
                          } catch (err) {
                            const msg = err.response?.data?.error;
                            if (msg === 'subscription_required') toast.error('A subscription is required to claim free items.');
                            else if (msg === 'You already claimed this item') toast.error('You already have an active claim on this item.');
                            else toast.error(msg || 'Failed to claim item');
                          } finally { setClaimLoading(false); }
                        }}>
                        {claimLoading ? 'Claiming...' : '🆓 Claim Item'}
                      </button>
                    )
                  ) : (
                    <button className="btn btn-cherry btn-full btn-lg" onClick={() => {
                      const extras = listing.listing_type === 'rent'
                        ? { startDate: rentDates.start, endDate: rentDates.end, rentDays: getRentDays(), rentToBuy: listing.rent_to_buy_enabled }
                        : {};
                      addToCart(listing, extras);
                      toast.success(listing.listing_type === 'rent' ? 'Booking added to cart! 📅' : 'Added to cart! 🛒');
                    }}>
                      {listing.listing_type === 'rent' ? '📅 Book Now' : '🛒 Add to Cart'}
                    </button>
                  )}
                  {/* Accepted offer — buy at offer price */}
                  {acceptedOffer && (
                    <div style={{ background:'#EAF5EC', border:'1px solid #81C784', borderRadius:12, padding:14, marginBottom:4 }}>
                      <div style={{ fontWeight:700, fontSize:13, color:'#2D7A3A', marginBottom:6 }}>✅ Your offer of ${parseFloat(acceptedOffer.amount).toFixed(2)} was accepted!</div>
                      <button className="btn btn-cherry btn-full" onClick={() => {
                        addToCart(listing, { offerPrice: acceptedOffer.amount, offerId: acceptedOffer.id });
                        toast.success('Added to cart at offer price!');
                        navigate('/checkout');
                      }}>🛒 Buy at ${parseFloat(acceptedOffer.amount).toFixed(2)}</button>
                    </div>
                  )}

                  {/* Make an Offer — sale listings only, not your own */}
                  {listing.listing_type === 'sale' && user && listing.seller_id !== user.id && !acceptedOffer && (
                    <div>
                      {!showOffer ? (
                        <button className="btn btn-ghost btn-full" onClick={() => setShowOffer(true)}>💰 Make an Offer</button>
                      ) : offerSent ? (
                        <div className="alert alert-success">✅ Offer sent! The seller will be notified.</div>
                      ) : (
                        <div style={{ background:'var(--cream)', borderRadius:12, padding:16, border:'1px solid var(--border)' }}>
                          <div style={{ fontWeight:700, marginBottom:10 }}>💰 Make an Offer</div>
                          <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                            <span style={{ alignSelf:'center', fontWeight:700 }}>$</span>
                            <input className="form-input" type="number" placeholder={`Listed at $${parseFloat(listing.price||0).toFixed(2)}`} value={offerAmount} onChange={e => setOfferAmount(e.target.value)} style={{ flex:1 }} />
                          </div>
                          <textarea className="form-input" placeholder="Add a note (optional)" value={offerMsg} onChange={e => setOfferMsg(e.target.value)} style={{ marginBottom:8, minHeight:60, resize:'vertical' }} />
                          <div style={{ display:'flex', gap:8 }}>
                            <button className="btn btn-cherry btn-sm" disabled={offerLoading || !offerAmount} onClick={async () => {
                              setOfferLoading(true);
                              try {
                                await API.post('/offers', { listingId: listing.id, amount: parseFloat(offerAmount), message: offerMsg });
                                setOfferSent(true);
                              } catch (err) { toast.error(err.response?.data?.error || 'Failed to send offer'); }
                              finally { setOfferLoading(false); }
                            }}>{offerLoading ? 'Sending...' : 'Send Offer'}</button>
                            <button className="btn btn-ghost btn-sm" onClick={() => setShowOffer(false)}>Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ display:'flex', gap:8 }}>
                    <button className="btn btn-ghost btn-sm" style={{ flex:1 }} onClick={() => { if(!user){navigate('/login');return;} setShowMsg(true); }}>
                      💬 Message
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={handleSave} disabled={saving} title="Save to favorites">
                      {saved ? '❤️' : '🤍'}
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={handleWatch} disabled={watchLoading} title={watching ? 'Stop watching for price drops' : 'Watch for price drops'}>
                      {watching ? '🔔' : '🔕'}
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  <button className="btn btn-ghost btn-full" onClick={() => navigate(`/listing/${id}/edit`)}>✏️ Edit Listing</button>
                </div>
              )}

              {/* Trust signals */}
              <div style={{ marginTop:16, display:'flex', flexDirection:'column', gap:6 }}>
                {['🔒 Escrow protected payment','💳 Card or 🪙 crypto accepted','📍 Safe public meetup spots','🛡️ Damage deposit included'].map(t=>(
                  <div key={t} style={{ fontSize:12, color:'var(--text-soft)', display:'flex', alignItems:'center', gap:6 }}>{t}</div>
                ))}
              </div>
            </div>
          </div>

          {/* Seller card */}
          <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:16, padding:20, marginTop:16 }}>
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:14 }}>
              <div style={{ width:44, height:44, borderRadius:'50%', background:'var(--cherry-light)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:700, color:'var(--cherry)', overflow:'hidden', flexShrink:0 }}>
                {listing.avatar_url
                  ? <img src={`${API_BASE}${listing.avatar_url}`} alt="" style={{ width:'100%',height:'100%',objectFit:'cover' }} />
                  : listing.first_name?.[0]?.toUpperCase()
                }
              </div>
              <div>
                <div style={{ fontWeight:700, fontSize:14 }}>{listing.first_name} {listing.last_name}</div>
                <div style={{ fontSize:12, color:'var(--text-soft)' }}>
                  {listing.id_verified && <span style={{ color:'var(--green)', fontWeight:600 }}>✅ ID Verified · </span>}
                  {listing.total_listings} listing{listing.total_listings!==1?'s':''}
                </div>
              </div>
              <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
                {user && listing.seller_id !== user.id && (
                  <button className={`btn btn-sm ${following ? 'btn-ghost' : 'btn-cherry'}`} onClick={() => {
                    API.post(`/users/${listing.seller_id}/follow`).then(r => setFollowing(r.data.following));
                  }}>{following ? '✅ Following' : '+ Follow'}</button>
                )}
                <button className="btn btn-ghost btn-sm" onClick={()=>navigate(`/profile/${listing.seller_id}`)}>View Profile</button>
              </div>
            </div>
            <StarRating rating={listing.rating_avg} count={listing.rating_count} onClick={() => navigate(`/profile/${listing.seller_id}`)} />
            {listing.member_since && <div style={{ fontSize:12, color:'var(--text-soft)', marginTop:6 }}>Member since {format(new Date(listing.member_since),'MMM yyyy')}</div>}
          </div>
        </div>
      </div>

      {/* Similar items */}
      {similar.length > 0 && (
        <div style={{ marginTop: 56 }}>
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:700, marginBottom:20 }}>
            Similar Items
          </h2>
          <div style={{ display:'flex', gap:16, overflowX:'auto', paddingBottom:12, scrollbarWidth:'thin' }}>
            {similar.map(item => {
              const distMi = (userCoords && item.lat && item.lng)
                ? haversineMi(userCoords.lat, userCoords.lng, parseFloat(item.lat), parseFloat(item.lng))
                : null;
              const isNear = distMi !== null && distMi <= nearbyMiles && item.local_pickup_only;
              return (
                <div key={item.id} onClick={() => navigate(`/listing/${item.slug || item.id}`)}
                  style={{ flexShrink:0, width:200, background:'white', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden', cursor:'pointer', transition:'transform .15s, box-shadow .15s' }}
                  onMouseEnter={e => { e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow='var(--shadow-md)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}
                >
                  <div style={{ height:150, background:'var(--cream)', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', position:'relative' }}>
                    {item.primary_image
                      ? <img src={`${API_BASE}${item.primary_image}`} alt={item.title} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                      : <span style={{ fontSize:48, opacity:.35 }}>📦</span>
                    }
                    {/* Listing type badge */}
                    <div style={{ position:'absolute', top:8, left:8, background: item.listing_type==='rent'?'#1A5276': item.listing_type==='auction'?'#C0392B':'#2D7A3A', color:'white', fontSize:9, fontWeight:700, padding:'2px 7px', borderRadius:5, letterSpacing:'1.2px', textTransform:'uppercase' }}>
                      {item.listing_type==='rent'?'Rent': item.listing_type==='auction'?'Auction':'Sale'}
                    </div>
                    {/* Near You badge */}
                    {isNear && (
                      <div style={{ position:'absolute', top:8, right:8, background:'rgba(26,82,118,.92)', color:'white', fontSize:9, fontWeight:700, padding:'2px 7px', borderRadius:5, letterSpacing:'1px', display:'flex', alignItems:'center', gap:3 }}>
                        📍 {Math.round(distMi)} mi
                      </div>
                    )}
                    {/* Auction countdown overlay */}
                    {item.listing_type === 'auction' && item.auction_ends_at && (
                      <div style={{ position:'absolute', bottom:0, left:0, right:0, background:'rgba(0,0,0,.65)', padding:'4px 8px' }}>
                        <SimilarCountdown endsAt={item.auction_ends_at} />
                      </div>
                    )}
                  </div>
                  <div style={{ padding:'12px 14px' }}>
                    <div style={{ fontSize:13, fontWeight:600, lineHeight:1.3, marginBottom:6, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
                      {item.title}
                    </div>
                    <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:16, color:'var(--cherry)' }}>
                      ${parseFloat(item.price||0).toLocaleString()}
                      {item.listing_type==='rent' && <span style={{ fontSize:11, fontWeight:400, color:'var(--text-soft)' }}>/day</span>}
                    </div>
                    {item.condition && (
                      <div style={{ fontSize:11, color:'var(--text-soft)', marginTop:4 }}>
                        {({ new:'New', like_new:'Like New', excellent:'Excellent', very_good:'Very Good', good:'Good', fair:'Fair' })[item.condition] || item.condition}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Price History + Sold Comps */}
      {(priceHistory.length > 1 || soldComps.length > 0) && (
        <div style={{ marginTop:48 }}>
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:700, marginBottom:20 }}>
            📊 Price Insights
          </h2>
          <div style={{ display:'grid', gridTemplateColumns: priceHistory.length > 1 && soldComps.length > 0 ? '1fr 1fr' : '1fr', gap:20 }}>

            {/* Price History */}
            {priceHistory.length > 1 && (
              <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:20 }}>
                <div style={{ fontWeight:700, fontSize:15, marginBottom:16 }}>Price History</div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {priceHistory.map((row, i) => {
                    const prev = i > 0 ? parseFloat(priceHistory[i-1].price) : null;
                    const curr = parseFloat(row.price);
                    const diff = prev !== null ? curr - prev : null;
                    return (
                      <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 10px', background:i===priceHistory.length-1?'var(--cherry-light)':'#fafafa', borderRadius:8, border:`1px solid ${i===priceHistory.length-1?'var(--cherry)':'var(--border)'}` }}>
                        <div>
                          <div style={{ fontWeight:700, fontSize:15, color: i===priceHistory.length-1?'var(--cherry)':'#333' }}>${parseFloat(row.price).toLocaleString()}</div>
                          <div style={{ fontSize:11, color:'var(--text-soft)' }}>{new Date(row.changed_at).toLocaleDateString()}</div>
                        </div>
                        {diff !== null && (
                          <span style={{ fontSize:12, fontWeight:600, color: diff<0?'#2D7A3A':'#C0392B', background: diff<0?'#E8F5E9':'#FEEBEB', padding:'2px 8px', borderRadius:100 }}>
                            {diff<0?'▼':'▲'} ${Math.abs(diff).toFixed(2)}
                          </span>
                        )}
                        {i===priceHistory.length-1 && <span style={{ fontSize:11, fontWeight:700, color:'var(--cherry)' }}>Current</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Sold Comps */}
            {soldComps.length > 0 && (
              <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:20 }}>
                <div style={{ fontWeight:700, fontSize:15, marginBottom:4 }}>Recently Sold (90 days)</div>
                <div style={{ fontSize:12, color:'var(--text-soft)', marginBottom:14 }}>Similar items in this category</div>
                {(() => {
                  const prices = soldComps.map(c => parseFloat(c.price)).filter(Boolean);
                  const avg = prices.length ? prices.reduce((a,b)=>a+b,0)/prices.length : null;
                  const min = prices.length ? Math.min(...prices) : null;
                  const max = prices.length ? Math.max(...prices) : null;
                  return avg ? (
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:16 }}>
                      {[{ label:'Avg', val:`$${avg.toFixed(0)}` }, { label:'Low', val:`$${min.toFixed(0)}` }, { label:'High', val:`$${max.toFixed(0)}` }].map(s => (
                        <div key={s.label} style={{ textAlign:'center', padding:'10px 8px', background:'var(--cream)', borderRadius:8 }}>
                          <div style={{ fontSize:16, fontWeight:700 }}>{s.val}</div>
                          <div style={{ fontSize:11, color:'var(--text-soft)' }}>{s.label}</div>
                        </div>
                      ))}
                    </div>
                  ) : null;
                })()}
                <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:220, overflowY:'auto' }}>
                  {soldComps.map((comp, i) => (
                    <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:13, padding:'6px 0', borderBottom:'1px solid var(--border)' }}>
                      <div style={{ flex:1, paddingRight:8, color:'var(--text-mid)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{comp.title}</div>
                      <div style={{ fontWeight:700, color:'#2D7A3A', flexShrink:0 }}>${parseFloat(comp.price).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Message modal */}
      {showMsg && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowMsg(false)}>
          <div className="modal">
            <button className="modal-close" onClick={()=>setShowMsg(false)}>✕</button>
            <h2 className="modal-title">Message {listing.first_name}</h2>
            <p className="modal-sub">About: {listing.title}</p>
            {msgSent ? (
              <div className="alert alert-success">✅ Message sent! They'll reply soon.</div>
            ) : (
              <>
                <textarea className="form-textarea" placeholder="Hi, I'm interested in this item..." value={msgBody} onChange={e=>setMsgBody(e.target.value)} style={{ marginBottom:14 }} />
                <button className="btn btn-cherry btn-full" onClick={handleSendMessage} disabled={!msgBody.trim()}>Send Message</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
